import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { when } from "lit/directives/when.js";

import { Watch } from "@/decorators/watch";
import { emit } from "@/internals/events";
import type { Theme } from "@/internals/theme";

import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";

import styles from "./atlas-character-counter.scss";
import type { CharacterCounterType } from "./types";

import "@/components/display/atlas-text/atlas-text";

export type CharacterCounterProps = AtlasElementProps & {
    "min": number;
    "max": number;
    "current": number;
    "type": CharacterCounterType;
    "highlight-over-max": boolean;
    "highlight-theme": Theme;
};

/**
 * @tag atlas-character-counter
 *
 * @dependency atlas-text
 *
 * @event {CharacterCounterTypeChangeEvent} atlas-character-counter-type-change - Evento disparado quando o tipo do contador de caracteres é alterado
 */
@customElement("atlas-character-counter")
export default class AtlasCharacterCounter extends AtlasElement {
    static styles = styles;

    /** Quantidade mínima de caracteres */
    @property({ type: Number }) min = 0;

    /** Quantidade máxima de caracteres */
    @property({ type: Number }) max = 0;

    /** Quantidade atual de caracteres */
    @property({ type: Number }) current = 0;

    /** Indica que o contador deve ser destacado quando a quantidade máxima de caracteres for ultrapassada */
    @property({ type: Boolean, attribute: "highlight-over-max" }) highlightOverMax = false;

    /** Tema do contador de caracteres quando a quantidade máxima de caracteres for ultrapassada */
    @property({ type: String, attribute: "highlight-theme" }) highlightTheme: Theme = "danger";

    /** @internal */
    @property({ type: String, reflect: true }) type: CharacterCounterType = "none";

    /** @internal */
    @Watch(["min", "max", "current"])
    public updateType() {
        const previousType = this.type;

        if (!!this.getMinValue() && this.isMissingChars()) {
            this.type = "missing";
        } else if (!!this.getMaxValue()) {
            this.type = "default";
        } else {
            this.type = "none";
        }

        if (this.type !== previousType) {
            emit(this, "atlas-character-counter-type-change", {
                trackDisable: true,
                detail: { type: this.type }
            });
        }
    }

    private getMaxValue() {
        return Math.max(this.max, 0);
    }

    private getMinValue() {
        return Math.max(this.min, 0);
    }

    private getCurrentValue() {
        return Math.max(this.current, 0);
    }

    private getDefaultContent() {
        const max = this.getMaxValue().toString().padStart(2, "0");
        const current = this.getCurrentValue().toString().padStart(2, "0");

        return `${current}/${max}`;
    }

    private getMissingContent() {
        const minValue = this.getMinValue();

        const missingChars = minValue - this.getCurrentValue();

        const isPlural = missingChars > 1;

        if (this.getCurrentValue() === 0) {
            return isPlural ? `Mínimo de ${minValue} caracteres` : `Mínimo de ${minValue} caractere`;
        }

        return isPlural ? `Faltam ${missingChars} caracteres` : `Falta ${missingChars} caractere`;
    }

    private getTheme(): Theme | undefined {
        if (!this.shouldHighlight()) return undefined;

        return this.highlightTheme ?? "danger";
    }

    private isMissingChars() {
        return this.getCurrentValue() < this.getMinValue();
    }

    private isExceededChars() {
        const maxValue = this.getMaxValue();

        if (!maxValue) return false;

        return this.getCurrentValue() > maxValue;
    }

    private isValidType() {
        return this.type === "missing" || this.type === "default";
    }

    private shouldHighlight() {
        return this.highlightOverMax && this.isExceededChars();
    }

    /** @internal @override */
    public render() {
        return when(
            this.isValidType(),
            () => html`
                <atlas-text
                    size="xs"
                    aria-live="polite"
                    ?muted=${!this.shouldHighlight()}
                    theme=${ifDefined(this.getTheme())}
                >
                    ${this.type === "missing" ? this.getMissingContent() : this.getDefaultContent()}
                </atlas-text>
            `
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-character-counter": AtlasCharacterCounter;
    }
}
